import { i18n, Messages } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import React from "react";
import { detect, fromNavigator, fromStorage, LocaleString } from "@lingui/detect-locale";
import { renderToString } from "react-dom/server";
import type { Locale as AntdLocale } from "antd/lib/locale";
import type { PickerLocale } from "antd/lib/date-picker/generatePicker";
import antdDeDE from "antd/lib/locale/de_DE";
import antdEnUS from "antd/lib/locale/en_US";

export { i18n };

const defaultLocale = "en";

const { locale, messages } = await detectAndImportLocale();
setLocale(locale, messages);

function setLocale(locale: string, messages: Messages) {
  i18n.loadAndActivate({ locale, messages });
  document.documentElement.setAttribute("lang", i18n.locale);
}

async function importMessages(locale: LocaleString) {
  switch (locale) {
    case "de":
      return await import("../locales/de.po");
    case "en":
      return await import("../locales/en.po");
    default:
      throw new Error(`Unsupported locale: ${locale}`);
  }
}

async function detectAndImportLocale() {
  try {
    const locale = detect(
      fromStorage("enit-hub-language"),
      fromNavigator(),
      () => defaultLocale
    )!.split("-")[0];
    const { messages } = await importMessages(locale);
    return { locale, messages };
  } catch {
    const { messages } = await importMessages(defaultLocale);
    return { locale: defaultLocale, messages };
  }
}

export function I18n({ children }: React.PropsWithChildren) {
  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}

export function renderTransToString(WrappedComponent: React.ReactNode) {
  return renderToString(<I18nProvider i18n={i18n}>{WrappedComponent}</I18nProvider>);
}

// ANTD
// Depending on how the bundler applies CJS/ESM interop, antd's locale modules
// resolve either to the locale itself or to a module namespace wrapping it as
// `default`. ConfigProvider unwraps that itself, but reading nested keys does not.
function unwrapAntdLocale(localeModule: AntdLocale): AntdLocale {
  const wrapped = (localeModule as { default?: AntdLocale }).default;
  return wrapped?.locale ? wrapped : localeModule;
}

// The bundled de_DE picker locale omits fieldDateFormat/fieldDateTimeFormat, so
// rc-picker falls back to its YYYY-MM-DD defaults. RangePicker resolves its locale
// from the `Calendar` key while DatePicker uses `DatePicker`, so both need patching.
function makeGermanLocale(): AntdLocale {
  const deDE = unwrapAntdLocale(antdDeDE);
  const pickerLocale = deDE.DatePicker;
  if (!pickerLocale) {
    return deDE;
  }
  const germanPickerLocale: PickerLocale = {
    ...pickerLocale,
    lang: {
      ...pickerLocale.lang,
      fieldDateFormat: "DD.MM.YYYY",
      fieldDateTimeFormat: "DD.MM.YYYY HH:mm:ss",
    },
  };
  return { ...deDE, DatePicker: germanPickerLocale, Calendar: germanPickerLocale };
}

const antdDeDEWithGermanDateFormats = makeGermanLocale();

export function getAntdLocale(localeStr: string) {
  return (localeStr || defaultLocale) === "de" ? antdDeDEWithGermanDateFormats : antdEnUS;
}
